import React, { useEffect } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { NameDto } from 'api/Common/Types'
import { CountryDto } from 'api/Identity/Types/Country'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AccountGroupDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { AccountType } from 'api/Trading-Accounts/Types/AccountType'
import { BookType } from 'api/Trading-Accounts/Types/BookType'
import { PlatformTypeDto } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { ServerType } from 'api/Trading-Accounts/Types/ServerType'
import { UpdateAccountGroup } from 'api/Trading-Accounts/Types/UpdateAccountGroup'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useAppDispatch } from 'store'
import { fetchServerAction } from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { FormSubmitValues } from 'utils/formValidation'
import { isArrayOfT } from 'utils/is-array-of'
import { hasPermission } from 'utils/permissionsValidator'

const AccountGroupDetailsFormUI: React.FC<FormikProps<AccountGroupFormValues> & OuterProps> = (
  props
) => {
  const {
    resetForm,
    handleSubmit,
    setFieldValue,
    setSubmitting,
    errors,
    isEdit,
    setEdit,
    values,
    servers,
    getAccountTypeOptions,
  } = props

  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSubmitForm = () => {
    setSubmitting(true)
    setEdit(false)
    handleSubmit()
  }

  useEffect(() => {
    getAccountTypeOptions({
      accountGroupTypeId: values.accountGroupTypeId,
      platformTypeId: values.platformTypeId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.accountGroupTypeId, values.platformTypeId])

  useEffect(() => {
    if (!props.tradingAccountType.length) return
    if (!props.tradingAccountType.some((x) => x.id === values.tradingAccountTypeId)) {
      setFieldValue('tradingAccountTypeId', props.tradingAccountType[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tradingAccountType])

  return (
    <Form onSubmit={handleSubmit}>
      <DataGrid
        columns={2}
        rightAddon={
          <ButtonGroup>
            {!isEdit ? (
              <Button
                type='button'
                onClick={() => setEdit(true)}
                disabled={!hasPermission(props.permissions, PermissionsEnum.CanEditAccountGroup)}
              >
                Edit
              </Button>
            ) : (
              <>
                <Button variant='outline' type='button' onClick={cancel}>
                  Cancel
                </Button>
                <Button type='button' onClick={handleSubmitForm}>
                  Save
                </Button>
              </>
            )}
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard title='Account Group Details' columns={2}>
            <FormSelectField
              name='tradingAccountTypeId'
              label='Trading Account Type'
              options={props.tradingAccountType}
              readOnly={!isEdit}
              error={errors.tradingAccountTypeId}
              dataTest='trading-account-type-field'
              onChange={(value) => setFieldValue('tradingAccountTypeId', Number(value))}
            />
            <FormSelectField
              name='platformTypeId'
              label='Platform'
              options={props.platformTypes}
              readOnly={!isEdit}
              error={errors.platformTypeId}
              dataTest='platform-field'
              onChange={(value) => {
                const nextPlatformTypeId = Number(value)
                setFieldValue('platformTypeId', nextPlatformTypeId)
                dispatch(fetchServerAction(nextPlatformTypeId, tradingAccountsApiClient))
                if (nextPlatformTypeId !== props.data.platformType.id) {
                  setFieldValue('servers', [])
                }
              }}
            />
            <FormReadonlyField
              name='currencyId'
              label='Currency'
              value={values.currencyId}
              dataTest='currency-field'
            />
            <FormSelectField
              name='accountGroupTypeId'
              label='Account Group Type'
              options={props.groupTypes}
              readOnly={!isEdit}
              error={errors.accountGroupTypeId}
              dataTest='account-group-type-field'
              onChange={(value) => setFieldValue('accountGroupTypeId', Number(value))}
            />
            <FormSelectField
              name='bookTypeId'
              label='Book'
              options={props.bookTypes}
              readOnly={!isEdit}
              error={errors.bookTypeId}
              dataTest='book-field'
              onChange={(value) => setFieldValue('bookTypeId', value ? Number(value) : undefined)}
            />
            <FormSelectField
              name='classificationTypeId'
              label='Classification'
              options={props.classificationTypes}
              readOnly={!isEdit}
              error={errors.classificationTypeId}
              dataTest='classification-field'
              onChange={(value) => setFieldValue('classificationTypeId', Number(value))}
            />
            <FormSelectField
              name='countryId'
              label='Country'
              options={props.countries}
              readOnly={!isEdit}
              dataTest='country-field'
              showSearch
              allowClear
              onChange={(value) => setFieldValue('countryId', value ? String(value) : null)}
            />
            <FormField
              name='externalId'
              label='External ID'
              readOnly={!isEdit}
              dataTest='external-id-field'
            />
            <FormReadonlyField
              name='tickmillCompany'
              label='Tickmill Company'
              value={values.tickmillCompany}
              dataTest='tickmill-company-field'
            />
            <FormCheckboxField
              name='isEnabled'
              label='Enabled'
              readOnly={!isEdit}
              dataTest='is-enabled-field'
            />
            <FormCheckboxField
              name='isSwapFree'
              label='Swap Free'
              readOnly={!isEdit}
              dataTest='is-swap-free-field'
            />
            <FormCheckboxField
              name='isStocks'
              label='Stocks'
              readOnly={!isEdit}
              dataTest='is-stocks-field'
            />
            <FormCheckboxField
              name='isFutures'
              label='Futures'
              readOnly={!isEdit}
              dataTest='is-futures-field'
            />
            <FormCheckboxField
              name='isPerpetuals'
              label='Perpetuals'
              readOnly={!isEdit}
              dataTest='is-perpetuals-field'
            />
            <FormCheckboxField
              name='canBeAttached'
              label='Can be attached'
              readOnly={!isEdit}
              dataTest='can-be-attached-field'
            />
            <FormCheckboxField
              name='canBeDirectAssigned'
              label='Can be direct assigned'
              readOnly={!isEdit}
              dataTest='can-be-direct-assigned-field'
            />
            <FormCheckboxField
              name='canBeIBAssigned'
              label='Can be IB assigned'
              readOnly={!isEdit}
              dataTest='can-be-ib-assigned-field'
            />
          </DataCard>

          <DataCard title='Servers'>
            <FormMultiSelectField
              name='servers'
              label='Servers'
              options={servers.map((server: ServerType) => ({
                id: server.id,
                name: server.publicName,
              }))}
              readOnly={!isEdit}
              showSearch
              allowClear
              dataTest='servers-field'
              value={Array.isArray(values.servers) ? values.servers : []}
              onChange={(value) => {
                const nextValue = Array.isArray(value)
                  ? value.map(String)
                  : value == null
                    ? []
                    : [String(value)]

                setFieldValue('servers', nextValue)
              }}
            />
          </DataCard>

          <DataCard title='Restrictions'>
            <FormMultiSelectField
              name='restrictions'
              label='Restrictions'
              options={props.tradingAccountRestrictions}
              readOnly={!isEdit}
              value={values.restrictions}
              onChange={(value) => {
                const nextValue = Array.isArray(value)
                  ? value.map(Number)
                  : value == null
                    ? []
                    : [Number(value)]

                props.setFieldValue('restrictions', nextValue)
              }}
            />
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

function dataToSubmit(values: AccountGroupFormValues): FormSubmitValues<UpdateAccountGroup> {
  return {
    ...values,
    bookTypeId: values?.bookTypeId || null,
    servers: isArrayOfT(values.servers) ? values.servers.map((x) => x.value) : values.servers,
  }
}

interface AccountGroupFormValues {
  name: string
  currencyId: CurrencyType
  tradingAccountTypeId: number
  platformTypeId: number
  accountGroupTypeId: number
  bookTypeId: number | undefined
  classificationTypeId: number
  countryId: string | null
  externalId: string
  isEnabled: boolean
  isSwapFree: boolean
  isStocks: boolean
  isFutures: boolean
  isPerpetuals: boolean
  canBeDirectAssigned: boolean
  canBeIBAssigned: boolean
  canBeAttached: boolean
  tickmillCompany: string
  servers: string[] | { label: string; value: string }[]
  restrictions: number[]
}

interface OuterProps {
  data: AccountGroupDto
  servers: ServerType[]
  isEdit: boolean
  tradingAccountType: AccountType[]
  platformTypes: PlatformTypeDto[]
  groupTypes: NameDto[]
  bookTypes: BookType[]
  classificationTypes: NameDto[]
  tradingAccountRestrictions: NameDto[]
  countries: CountryDto[]
  permissions: PermissionDto[]

  setEdit(val: boolean): void
  getAccountTypeOptions: (props: {
    platformTypeId: number
    accountGroupTypeId: number
  }) => Promise<void>
  onSubmit(values: FormSubmitValues<UpdateAccountGroup>): Promise<void>
}

export const AccountGroupDetailsForm = withFormik<OuterProps, AccountGroupFormValues>({
  mapPropsToValues: ({ data }) => {
    return {
      name: data.name,
      currencyId: data.currency.id as CurrencyType,
      tradingAccountTypeId: data.tradingAccountType.id,
      platformTypeId: data.platformType.id,
      accountGroupTypeId: data.accountGroupType.id,
      bookTypeId: data.bookType?.id,
      classificationTypeId: data.classificationType?.id,
      countryId: data.country?.id || null,
      externalId: data.externalId,
      isEnabled: data.isEnabled,
      isSwapFree: data.isSwapFree,
      isStocks: data.isStocks ?? false,
      isFutures: data.isFutures ?? false,
      isPerpetuals: data.isPerpetuals ?? false,
      canBeDirectAssigned: data.canBeDirectAssigned,
      canBeIBAssigned: data.canBeIBAssigned,
      canBeAttached: data.canBeAttached,
      servers: (data.servers || []).map((srv) => srv.id),
      tickmillCompany: data.tickmillCompany?.name,
      restrictions: data.restrictions?.map((x) => x.id) || [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AccountGroupFormValues> = {}
    if (!values.classificationTypeId) {
      errors.classificationTypeId = 'required'
    }
    if (!values.accountGroupTypeId) {
      errors.accountGroupTypeId = 'required'
    }
    if (!values.bookTypeId) {
      errors.bookTypeId = 'required'
    }
    if (!values.platformTypeId) {
      errors.platformTypeId = 'required'
    }
    if (!values.tradingAccountTypeId) {
      errors.tradingAccountTypeId = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(AccountGroupDetailsFormUI)
